import axios from "axios";
import { sendContactForm } from "@/core/api/apiClient";
import { ContactFormData, ContactFormResponse } from "./types";

// Cache for rate limiting and duplicate prevention
const submissionCache = new Map<string, number>();
const CACHE_DURATION = 30000; // 30 seconds

/**
 * Prevents duplicate submissions within a short time frame
 */
const isDuplicateSubmission = (email: string): boolean => {
  const now = Date.now();
  const lastSubmission = submissionCache.get(email);

  if (lastSubmission && now - lastSubmission < CACHE_DURATION) {
    return true;
  }

  submissionCache.set(email, now);

  // Clean up old entries
  for (const [cachedEmail, timestamp] of submissionCache.entries()) {
    if (now - timestamp > CACHE_DURATION) {
      submissionCache.delete(cachedEmail);
    }
  }

  return false;
};

/**
 * Enhanced email validation
 */
const validateEmail = (email: string): boolean => {
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email) && email.length <= 254;
};

/**
 * Sanitize input data to prevent XSS and ensure data quality
 */
const sanitizeFormData = (formData: ContactFormData): ContactFormData => {
  return {
    firstName: formData.firstName.trim().slice(0, 50),
    lastName: formData.lastName.trim().slice(0, 50),
    email: formData.email.trim().toLowerCase().slice(0, 254),
    phone: formData.phone?.trim().slice(0, 20) || "",
    subject: formData.subject.trim(),
    message: formData.message.trim().slice(0, 2000),
    privacyConsent: formData.privacyConsent || false,
  };
};

/**
 * Submit the contact form with enhanced error handling and SEO optimization
 */
export const submitContactForm = async (
  formData: ContactFormData
): Promise<ContactFormResponse> => {
  try {
    // Input validation
    if (!formData.email?.trim()) {
      return {
        success: false,
        message: "Email address is required.",
      };
    }

    if (!validateEmail(formData.email.trim())) {
      return {
        success: false,
        message: "Please enter a valid email address.",
      };
    }

    // Check for duplicate submissions
    if (isDuplicateSubmission(formData.email.trim().toLowerCase())) {
      return {
        success: false,
        message: "Please wait before submitting another message.",
      };
    }

    // Sanitize input data
    const sanitizedData = sanitizeFormData(formData);

    // Format data to match what the Lambda function expects
    const payload = {
      first_name: sanitizedData.firstName,
      last_name: sanitizedData.lastName,
      email: sanitizedData.email,
      phone: sanitizedData.phone,
      subject: sanitizedData.subject,
      message: sanitizedData.message,
      privacy_consent: sanitizedData.privacyConsent,
      source: "website_contact_form",
      user_agent: navigator.userAgent,
      referrer: document.referrer || "direct",
      page_url: window.location.href,
      timestamp: new Date().toISOString(),
    };

    console.log("📧 Submitting contact form:", {
      email: payload.email,
      subject: payload.subject,
      source: payload.source,
    });

    const response = await sendContactForm(payload);

    if (response.status === 200 || response.status === 201) {
      console.log("✅ Contact form submitted successfully");

      return {
        success: true,
        message:
          "Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.",
        formId:
          response.data?.submission_id ||
          response.data?.id ||
          `contact_${Date.now()}`,
      };
    } else {
      console.error("❌ Contact form submission failed:", response.status);
      return {
        success: false,
        message: "There was an error submitting your form. Please try again.",
      };
    }
  } catch (error: any) {
    console.error("Contact form submission error:", error);

    if (axios.isAxiosError(error) && error.response) {
      const status = error.response.status;

      // Handle specific HTTP status codes
      switch (status) {
        case 400:
          return {
            success: false,
            message: "Please check your information and try again.",
            errors: error.response.data?.errors || {},
          };
        case 429:
          return {
            success: false,
            message: "Too many requests. Please try again in a few minutes.",
          };
        case 500:
          return {
            success: false,
            message:
              "Our server is temporarily unavailable. Please try again later.",
          };
        default:
          return {
            success: false,
            message:
              "There was an error processing your request. Please try again.",
          };
      }
    } else if (error.request) {
      return {
        success: false,
        message:
          "Unable to connect to our server. Please check your internet connection and try again.",
      };
    }

    // Generic error fallback
    return {
      success: false,
      message:
        "We're experiencing technical difficulties. Please try again later or contact us directly by phone at (202) 340-8277.",
    };
  }
};

/**
 * Clear submission cache (useful for testing or manual cleanup)
 */
export const clearSubmissionCache = (): void => {
  submissionCache.clear();
};
